import { isEmpty, isNil, uniq } from 'lodash';
import { createSelector } from 'reselect';
import {
  isActivePlus,
  isBrokerage,
  products,
  isActiveAdvisory,
  productMap,
  COLLEGE_SAVINGS_CLASS,
  isCollegeSavings
} from 'services/Products';
import { difference, percentChange } from 'services/Calculation';
import {
  getActiveAccounts,
  getNonPurgedAccounts,
  getActiveHouseholdAccountsWithBalance,
  getBrokerageAccountsFromSweepMap,
  getMmrMessages
} from 'state/account/AccountSelectors';
import { extractAndFormatOwners } from 'services/Accounts';
import {
  BLANK_CELL_PLACEHOLDER,
  EMPTY_CELL_DASH,
  CASH_ROW_NAME
} from 'services/TableUtils';
import {
  createColumnDef,
  orderedColumnKeys,
  renderingData,
  hiddenColumns
} from 'components/layouts/Account/ColumnDefs';
import {
  has529Only,
  hasAlaska529Only,
  getView
} from 'state/view/ViewSelectors';
import {
  filterFunction,
  sortingFunction
} from 'services/Export/ExportAccounts';
import { shareClassConversionEnabled } from 'services/FeatureFlags';

export const getHistoricalBalances = (state) => state.historicalBalances;
const getAccountPerformanceData = (state) => state.accountPerformanceMap;
export const getAccountsGridPrintLayout = (state) =>
  state.accountsGridPrintLayout;
export const getGridFilteringSelections = (state) =>
  state.accountsGridFilteringSelections;

export const getAdviceGridAccounts = createSelector(
  getNonPurgedAccounts,
  getActiveHouseholdAccountsWithBalance,
  getAccountPerformanceData,
  getHistoricalBalances,
  getBrokerageAccountsFromSweepMap,
  getMmrMessages,
  (
    allAccounts,
    visibleAccounts,
    performance = [],
    historicalBalances,
    linkedBrokerageAccountsMap,
    mmrMessages
  ) => {
    return visibleAccounts.reduce((arr, account) => {
      const { messages } = mmrMessages;
      const {
        positions,
        cash,
        balanceValue,
        previousBalanceValue,
        accountName,
        ticker,
        accountNumber,
        accountChecksum,
        owner,
        owners,
        accountType,
        productType,
        accountId,
        hiddenAccount,
        retirementAccount,
        isViewOnly,
        isRelated,
        isMoneyMarket,
        sweepAccount,
        sweepAccountId,
        inceptionDate,
        balanceAsOfDate,
        intraDayAsOfDate,
        shareClassConversion
      } = account;
      const mmrMessage = messages ? messages.get(ticker) : '';
      const sweepAccountFromMap = linkedBrokerageAccountsMap[accountId];
      const sweepAccountWChecksum =
        sweepAccountFromMap &&
        sweepAccount &&
        `${sweepAccountFromMap.accountNumber}-${sweepAccountFromMap.accountChecksum}`;
      const isActivePlusProduct = isActivePlus(productType);
      const isBrokerageProduct = isBrokerage(productType);
      const isActiveAdvisoryProduct = isActiveAdvisory(productType);
      const isPositionProduct =
        isActivePlusProduct || isBrokerageProduct || isActiveAdvisoryProduct;
      const showDash = historicalBalances.selectedDate;
      const activeShareClassConverted = shareClassConversionEnabled() &&
        shareClassConversion &&
        shareClassConversion.activeShareClassConverted

      let accountDescriptor = `${accountName}${ticker ? ` - ${ticker}` : ''}`;
      if (isActivePlusProduct) {
        accountDescriptor = accountName;
      }
      const dailyPriceChangeSum = showDash
        ? EMPTY_CELL_DASH
        : positions.reduce((sum, position) => sum + position.priceChange, 0);
      const priceChangePercentSum = showDash
        ? EMPTY_CELL_DASH
        : percentChange(balanceValue, previousBalanceValue);
      const tag = sweepAccount
        ? ' <span class="tag-label">Sweep</span>'
        : isViewOnly
        ? ' <span class="tag-label">View Only</span>'
        : activeShareClassConverted
        ? ' <span class="tag-label">Share Class Conversion</span>'
        : '';
      const accountNameAndNumber = `${accountName}${
        ticker ? ' - ' + ticker : ''
      }${tag}<br />${accountNumber}-${accountChecksum}`.trim();
      const accountNumberWChecksum = `${accountNumber}-${accountChecksum}`;
      const diff = historicalBalances.selectedDate
        ? EMPTY_CELL_DASH
        : difference(balanceValue, previousBalanceValue);
      const isOwner = owner
        ? 'Owned'
        : isRelated
        ? 'Related'
        : isViewOnly
        ? 'View Only'
        : '';
      const formattedOwners = extractAndFormatOwners(account);
      const primaryOwner = `${accountType}\n${formattedOwners}`;
      const balance =
        historicalBalances.selectedDate && historicalBalances.balances
          ? historicalBalances.balances.find(
              (currBalance) => currBalance.accountId === accountId
            ).endingMarketValue
          : balanceValue || 0;
      const personalInvestment = 'Personal Investments';
      const inception = performance[accountId]
        ? !isNil(performance[accountId].inceptionReturn) &&
          !historicalBalances.selectedDate
          ? performance[accountId].inceptionReturn
          : historicalBalances.selectedDate
          ? EMPTY_CELL_DASH
          : null
        : historicalBalances.selectedDate
        ? EMPTY_CELL_DASH
        : null;
      const performanceInceptionDate = performance[accountId]
        ? isActivePlus(productType)
          ? inceptionDate
          : performance[accountId].inceptionDate || EMPTY_CELL_DASH
        : EMPTY_CELL_DASH;

      return [
        ...arr,
        {
          accountDescriptor,
          accountName,
          accountNumber,
          accountNumberWChecksum,
          accountType,
          balance,
          balanceValue,
          cash,
          previousBalanceValue,
          difference: diff,
          groupId: isPositionProduct ? accountId : null,
          isPositionProduct,
          priceChangePercentSum,
          dailyPriceChangeSum,
          hiddenAccount,
          retirementAccount,
          productType,
          positions,
          accountNameAndNumber,
          owner,
          owners,
          isOwner,
          primaryOwner,
          performance: inception,
          performanceInceptionDate,
          personalInvestment,
          isActivePlusOrBrokerage: isActivePlusProduct || isBrokerageProduct,
          accountId,
          isViewOnly,
          isRelated,
          isMoneyMarket,
          sweepAccount,
          sweepAccountId,
          sweepAccountWChecksum,
          ticker,
          inceptionDate,
          balanceAsOfDate,
          mmrMessage,
          intraDayAsOfDate,
          activeShareClassConverted
        }
      ];
    }, []);
  }
);

export const updatePositionsDataForAdviceGridAccounts = createSelector(
  getAdviceGridAccounts,
  getAccountPerformanceData,
  getHistoricalBalances,
  (accounts, performance, historicalBalances) => {
    return accounts.map((account) => {
      const { positions, cash, productType, intraDayAsOfDate } = account;

      const isActivePlusProduct = isActivePlus(productType);
      const isBrokerageProduct = isBrokerage(productType);

      // positions data didn't have correctly calculated difference/daily change % values
      const updatedPositions = positions.reduce((arr, position) => {
        const {
          currentBalance,
          investmentVehicleId,
          previousBalance,
          priceChange,
          price,
          quantity
        } = position;
        const showDash =
          historicalBalances.selectedDate ||
          (intraDayAsOfDate &&
            isBrokerageProduct &&
            investmentVehicleId === -1);
        return [
          ...arr,
          {
            ...position,
            name: `${position.name} - ${position.ticker}`,
            difference: historicalBalances.selectedDate
              ? EMPTY_CELL_DASH
              : difference(currentBalance, previousBalance),
            priceChangePercent: showDash
              ? EMPTY_CELL_DASH
              : percentChange(currentBalance, previousBalance),
            currentBalance: historicalBalances.selectedDate
              ? EMPTY_CELL_DASH
              : currentBalance,
            priceChange: showDash ? EMPTY_CELL_DASH : priceChange,
            price: historicalBalances.selectedDate ? EMPTY_CELL_DASH : price,
            quantity: historicalBalances.selectedDate
              ? EMPTY_CELL_DASH
              : quantity,
            isActivePlusOrBrokerage: isActivePlusProduct || isBrokerageProduct
          }
        ];
      }, []);

      // While not technically a position, the Cash bucket should be displayed like one.
      if (!isEmpty(cash)) {
        const currentBalance =
          cash.currentBalance && cash.currentBalance > 0
            ? cash.currentBalance
            : 0;
        updatedPositions.push({
          name: CASH_ROW_NAME,
          difference: historicalBalances.selectedDate
            ? EMPTY_CELL_DASH
            : difference(currentBalance, cash.previousBalance),
          priceChangePercent: historicalBalances.selectedDate
            ? EMPTY_CELL_DASH
            : percentChange(currentBalance, cash.previousBalance),
          currentBalance: historicalBalances.selectedDate
            ? EMPTY_CELL_DASH
            : currentBalance,
          priceChange: BLANK_CELL_PLACEHOLDER,
          previousBalance: cash.previousBalance,
          price: BLANK_CELL_PLACEHOLDER,
          quantity: BLANK_CELL_PLACEHOLDER
        });
      }

      const pos = historicalBalances.selectedDate
        ? { quantitySum: EMPTY_CELL_DASH, priceSum: EMPTY_CELL_DASH }
        : positions.reduce(
            (sums, position) => {
              return {
                quantitySum: sums.quantitySum + position.quantity,
                priceSum: sums.priceSum + position.price
              };
            },
            { quantitySum: 0, priceSum: 0 }
          );

      return {
        ...pos,
        ...account,
        positions: updatedPositions
      };
    });
  }
);

export const getAccountsGridTableData = createSelector(
  updatePositionsDataForAdviceGridAccounts,
  (accounts) => {
    return accounts.reduce((arr, account) => {
      const {
        accountId,
        accountType,
        positions,
        isPositionProduct,
        retirementAccount,
        personalInvestment,
        productType,
        isOwner,
        isRelated,
        isViewOnly,
        isMoneyMarket,
        owner,
        primaryOwner,
        hiddenAccount
      } = account;

      if (isPositionProduct) {
        if (positions.length) {
          positions.forEach((position) => {
            if (position) {
              const {
                name,
                currentBalance,
                previousBalance,
                price,
                priceChange,
                quantity,
                cusip
              } = position;
              const isCashRow = name === CASH_ROW_NAME;
              arr.push({
                accountId,
                retirementAccount,
                productType,
                isOwner,
                personalInvestment,
                isViewOnly,
                isRelated,
                isMoneyMarket,
                owner,
                primaryOwner,
                hiddenAccount,
                accountType,
                cusip,
                ticker: position?.ticker,
                groupId: accountId,
                quantitySum: isCashRow ? BLANK_CELL_PLACEHOLDER : quantity,
                balance: currentBalance,
                difference: difference(currentBalance, previousBalance),
                previousBalanceValue: previousBalance,
                priceSum: isCashRow ? BLANK_CELL_PLACEHOLDER : price,
                dailyPriceChangeSum: isCashRow
                  ? BLANK_CELL_PLACEHOLDER
                  : priceChange,
                accountName: name,
                accountNameAndNumber: name,
                accountDescriptor: name,
                isPosition: true
              });
            }
          });
        }
      } else {
        arr.push(account);
      }
      return arr;
    }, []);
  }
);

export const getCsvDownloadAccounts = createSelector(
  getAdviceGridAccounts,
  getGridFilteringSelections,
  (accounts, filteringSelections) => {
    const {
      accountType: {
        selection: accountTypeSelection,
        options: accountTypeOptions
      },
      sorting: { selection: sortingSelection }
    } = filteringSelections;

    const productSelection = productMap[accountTypeSelection];
    const isCollegeSavingsSelection =
      accountTypeSelection === COLLEGE_SAVINGS_CLASS;

    if (accountTypeSelection.includes('custom')) {
      const selectedCustom = accountTypeOptions.find(
        (option) => option.value === accountTypeSelection
      );
      const filtered = accounts.filter((a) =>
        selectedCustom.accountDescriptor.includes(a.accountId)
      );
      return sortingFunction(sortingSelection, filtered);
    }

    if (productSelection) {
      const filtered = accounts.filter(
        (a) => a.productType === productSelection
      );
      return sortingFunction(sortingSelection, filtered);
    }

    if (isCollegeSavingsSelection) {
      const filtered = accounts.filter((a) => isCollegeSavings(a.productType));
      return sortingFunction(sortingSelection, filtered);
    }

    const filtered = accounts.filter((a) =>
      filterFunction(accountTypeSelection, a)
    );
    return sortingFunction(sortingSelection, filtered);
  }
);

const getTypes = (accounts) => {
  const typesWithDuplicates = accounts.reduce((types, account) => {
    const product = products.byAccount[account.productType].key;
    types.push(product);
    account.retirementAccount
      ? types.push('retirement')
      : types.push('nonRetirement');
    if (account.isViewOnly) {
      types.push('viewOnly');
    }
    if (account.isRelated) {
      types.push('related');
    }
    if (account.owner) {
      types.push('owned');
    }
    return types;
  }, []);
  return [...new Set(typesWithDuplicates)];
};

export const getAdviceGridAccountTypes = createSelector(
  updatePositionsDataForAdviceGridAccounts,
  (accounts) => {
    return getTypes(accounts);
  }
);

export const getAccountTypes = createSelector(
  getNonPurgedAccounts,
  (accounts) => {
    return getTypes(accounts);
  }
);

export const getAccountTypesWithoutHidden = createSelector(
  getActiveAccounts,
  (accounts) => {
    return getTypes(accounts);
  }
);

const generateColumnDefs = (
  accounts,
  retirementOrder = false,
  has529Only = false,
  hasAlaska529Only = false,
  historicalBalances,
  view,
  gridFilteringSelections
) => {
  const { selectedDate: historicalBalanceView } = historicalBalances;

  const {
    mobile: { selection }
  } = gridFilteringSelections;

  const { isMobileLayout: isMobileView } = view;

  if (isEmpty(accounts)) {
    return [];
  }

  const uniqColumnKeys = uniq(orderedColumnKeys);
  let columns = uniqColumnKeys.map((field) =>
    createColumnDef(
      retirementOrder,
      has529Only,
      hasAlaska529Only,
      field,
      historicalBalances
    )
  );

  if (historicalBalanceView) {
    const hiddenHistoricalColumns = [
      'Quantity',
      'Price',
      'Daily Price Change',
      'Daily Change ($)',
      'Daily Change (%)',
      'performance'
    ];
    const filterField = (col) => col.headerName || col.field;
    const historicalColumns = columns.filter(
      (col) => !hiddenHistoricalColumns.includes(filterField(col))
    );
    return uniq(historicalColumns).sort((a, b) => a.order - b.order);
  }

  if (isMobileView) {
    columns = columns.filter(
      (def) =>
        def.field == selection ||
        renderingData.includes(def.field) ||
        hiddenColumns.includes(def.field)
    );
  }

  return uniq(columns).sort((a, b) => a.order - b.order);
};

export const getColumnDefs = createSelector(
  updatePositionsDataForAdviceGridAccounts,
  has529Only,
  hasAlaska529Only,
  getHistoricalBalances,
  getView,
  getGridFilteringSelections,
  (
    accounts,
    has529Only,
    hasAlaska529Only,
    historicalBalances,
    view,
    gridFilteringSelections
  ) => {
    return generateColumnDefs(
      accounts,
      false,
      has529Only,
      hasAlaska529Only,
      historicalBalances,
      view,
      gridFilteringSelections
    );
  }
);

export const getColumnDefsRetirementOrder = createSelector(
  updatePositionsDataForAdviceGridAccounts,
  has529Only,
  hasAlaska529Only,
  getHistoricalBalances,
  getView,
  getGridFilteringSelections,
  (
    accounts,
    has529Only,
    hasAlaska529Only,
    historicalBalances,
    view,
    gridFilteringSelections
  ) => {
    return generateColumnDefs(
      accounts,
      true,
      has529Only,
      hasAlaska529Only,
      historicalBalances,
      view,
      gridFilteringSelections
    );
  }
);
